import { useState } from 'react';
import { MoreVertical, Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { IconButton, Menu, MenuItem } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';

export interface ChatActionsMenuProps {
  /** Names the menu for assistive tech, since the trigger is icon-only. */
  chatTitle: string;
  pinned: boolean;
  /** Omitted where the caller offers pinning as its own control beside the menu. */
  onPin?: () => void;
  onRename: () => void;
  onDelete: () => void;
  triggerClassName?: string;
  align?: 'start' | 'end';
}

/**
 * Rename/pin/delete menu for a conversation, shared by the sidebar row and the
 * `/ui/chats` list. Built on `Menu` rather than `Popover` so the panel can
 * escape the row's own overflow and stacking context.
 */
export function ChatActionsMenu({
  chatTitle,
  pinned,
  onPin,
  onRename,
  onDelete,
  triggerClassName,
  align = 'start',
}: ChatActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const ariaLabel = `Actions for ${chatTitle}`;

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      side="right"
      align={align}
      aria-label={ariaLabel}
      contentClassName="w-[160px] ring-offset-v2-sidebar"
      trigger={
        <IconButton
          size="2xs"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'rounded-v2-sm text-v2-sidebar-foreground/60',
            'opacity-0 transition-[opacity,background-color,transform] group-hover:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100',
            // The sidebar's own accent in place of the button wash, open or hovered.
            'hover:bg-v2-sidebar-accent hover:text-v2-sidebar-foreground aria-expanded:bg-v2-sidebar-accent',
            open && 'opacity-100',
            triggerClassName,
          )}
        >
          <MoreVertical size={14} />
        </IconButton>
      }
    >
      {onPin ? (
        <MenuItem label={pinned ? 'Unpin' : 'Pin'} onClick={onPin}>
          {pinned ? <PinOff size={14} className="shrink-0" /> : <Pin size={14} className="shrink-0" />}
          {pinned ? 'Unpin' : 'Pin'}
        </MenuItem>
      ) : null}
      <MenuItem label="Rename" onClick={onRename}>
        <Pencil size={14} className="shrink-0" />
        Rename
      </MenuItem>
      <MenuItem label="Delete" destructive onClick={onDelete}>
        <Trash2 size={14} className="shrink-0" />
        Delete
      </MenuItem>
    </Menu>
  );
}
